/**
 * Grid Utilities (Story 5.4, Tasks 3, 5)
 *
 * Grid math for the 12-column report canvas:
 * - Occupancy matrix (2D array tracking which widget occupies each cell)
 * - Collision detection (prevents widget overlap)
 * - Next available position finder (row-first, left-to-right scan)
 * - Widget reflow (displaced widgets shift DOWN, never auto-shrink)
 * - Page break calculation for A4 PDF output
 */

import type { WidgetConfig } from "./templateUtils";

export const GRID_COLUMNS = 12;
export const GRID_GAP = 16; // px (Tailwind gap-4)
/** Maximum widgets allowed per report (Story 5.6) */
export const MAX_WIDGETS = 30;

// ── Row Height Constants (Task 5.1) ─────────────────────────────────

/** Small widget height: 1 rowSpan unit = 80px */
export const ROW_HEIGHT_SMALL = 80;
/** Medium widget height: 2 rowSpan units = 160px */
export const ROW_HEIGHT_MEDIUM = 160;
/** Large widget height: 3 rowSpan units = 240px */
export const ROW_HEIGHT_LARGE = 240;
/** Base row height unit (1 rowSpan = 80px) */
export const ROW_HEIGHT_UNIT = ROW_HEIGHT_SMALL;

// ── A4 Page Constants (Task 5.2) ────────────────────────────────────

/** A4 portrait: 595pt x 842pt */
/** Margins: 72pt top/bottom, 54pt sides */
/** Usable content: 487pt x 698pt → at 96dpi: ~649px x ~931px */
export const A4_CONTENT_WIDTH_PX = 649;
export const A4_CONTENT_HEIGHT_PX = 931;

/**
 * Valid column spans for the 12-column grid.
 * All divide 12 evenly: 1, 2, 3, 4, 6, 12.
 */
// Every column width 1–12 is valid, so resize snaps in uniform 1-column steps
// (previously [1,2,3,4,6,12], which left big 6→12 jumps — fewer widths the larger
// you went). A 12-column grid means any 1–12 span still tiles cleanly.
export const VALID_COL_SPANS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;
export type ValidColSpan = (typeof VALID_COL_SPANS)[number];

/**
 * Snaps a raw column span to the nearest valid value.
 */
export function snapToValidSpan(rawSpan: number): ValidColSpan {
  return VALID_COL_SPANS.reduce((prev, curr) =>
    Math.abs(curr - rawSpan) < Math.abs(prev - rawSpan) ? curr : prev
  );
}

/** Minimum colSpan per widget type. Widgets below this threshold snap up. */
const MIN_COL_SPAN: Partial<Record<string, ValidColSpan>> = {
  "top-posts": 4,
};

/**
 * Enforces minimum colSpan for widget types that require it.
 * Returns the colSpan unchanged if it meets the minimum, or snaps to minimum.
 */
export function enforceMinColSpan(widgetType: string, colSpan: ValidColSpan): ValidColSpan {
  const min = MIN_COL_SPAN[widgetType];
  if (min && colSpan < min) return min;
  return colSpan;
}

// ── Grid Occupancy Matrix ───────────────────────────────────────────

/**
 * Builds a 2D occupancy matrix from widget positions.
 * Each cell contains the widget ID occupying it, or null if empty.
 *
 * Matrix[row][col] = widgetId | null
 */
export function calculateGridPlacement(
  widgets: WidgetConfig[]
): (string | null)[][] {
  if (widgets.length === 0) return [];

  // Determine matrix dimensions
  const maxRow = Math.max(
    ...widgets.map((w) => w.position.row + (w.position.rowSpan ?? 1))
  );
  // Initialize matrix with nulls
  const matrix: (string | null)[][] = Array.from({ length: maxRow }, () =>
    Array.from({ length: GRID_COLUMNS }, () => null)
  );

  // Place each widget
  for (const widget of widgets) {
    const { row, col, colSpan, rowSpan = 1 } = widget.position;
    for (let r = row; r < row + rowSpan && r < maxRow; r++) {
      for (let c = col; c < col + colSpan && c < GRID_COLUMNS; c++) {
        matrix[r]![c] = widget.id;
      }
    }
  }

  return matrix;
}

// ── Collision Detection ─────────────────────────────────────────────

/**
 * Checks if placing a widget would overlap with existing widgets
 * or exceed grid boundaries.
 */
export function hasCollision(
  matrix: (string | null)[][],
  widget: WidgetConfig
): boolean {
  const { row, col, colSpan, rowSpan = 1 } = widget.position;

  // Boundary check: col + colSpan must not exceed 12
  if (col + colSpan > GRID_COLUMNS) return true;

  // Check each cell the widget would occupy
  for (let r = row; r < row + rowSpan; r++) {
    for (let c = col; c < col + colSpan; c++) {
      // If the row exists in the matrix and the cell is occupied
      if (r < matrix.length && matrix[r]![c] !== null) {
        return true;
      }
    }
  }

  return false;
}

// ── Next Available Position ─────────────────────────────────────────

export interface GridPosition {
  row: number;
  col: number;
}

/**
 * Finds the next available position for a widget of given colSpan and rowSpan.
 * Scans row-first, left-to-right to find a contiguous free slot.
 * Checks all rows the widget would occupy (not just the first).
 *
 * @param reservedRows - Number of rows reserved at the top (e.g. 3 for cover page).
 *   When > 0, skips rows 0..(reservedRows-1) for placement.
 */
export function getNextAvailablePosition(
  matrix: (string | null)[][],
  colSpan: number,
  rowSpan = 1,
  reservedRows = 0
): GridPosition {
  const startRow = Math.max(0, reservedRows);
  const maxRows = matrix.length + rowSpan; // Allow placing beyond matrix

  for (let row = startRow; row < maxRows; row++) {
    for (let col = 0; col <= GRID_COLUMNS - colSpan; col++) {
      // Check all cells the widget would occupy (colSpan x rowSpan)
      let free = true;
      for (let r = row; r < row + rowSpan && free; r++) {
        for (let c = col; c < col + colSpan; c++) {
          if (r < matrix.length && matrix[r]![c] !== null) {
            free = false;
            break;
          }
        }
      }
      if (free) {
        return { row, col };
      }
    }
  }

  // Fallback: place in a new row beyond the matrix
  return { row: Math.max(matrix.length, startRow), col: 0 };
}

// ── Widget Reflow ───────────────────────────────────────────────────

/**
 * Reflows widgets after a resize operation.
 *
 * Strategy:
 * 1. Place the changed widget at its new position
 * 2. For all other widgets, check if they collide with the changed widget
 * 3. Displaced widgets shift DOWN (not sideways), preserving their colSpan
 * 4. Use row-first, left-to-right scan to find next available position
 */
/** Max iterations for reflow to prevent infinite loops (Story 5.6 safety guard) */
const REFLOW_MAX_ITERATIONS = 100;

/**
 * @param reservedRows - Number of rows reserved at the top (e.g. 3 for cover page).
 *   Displaced widgets will not be reflowed into the reserved zone.
 */
export function reflowWidgets(
  widgets: WidgetConfig[],
  changed: WidgetConfig,
  reservedRows = 0
): WidgetConfig[] {
  // Start with the changed widget
  const result: WidgetConfig[] = [changed];

  // Build matrix with only the changed widget
  let matrix = calculateGridPlacement([changed]);

  // Process remaining widgets in their original order
  const others = widgets.filter((w) => w.id !== changed.id);
  let iterations = 0;

  for (const widget of others) {
    if (++iterations > REFLOW_MAX_ITERATIONS) {
      console.warn("reflowWidgets: max iteration limit reached, returning best-effort result");
      result.push(widget);
      continue;
    }

    // Extend matrix if needed for collision check
    const neededRows = widget.position.row + (widget.position.rowSpan ?? 1);
    while (matrix.length < neededRows) {
      matrix.push(Array.from({ length: GRID_COLUMNS }, () => null));
    }

    if (hasCollision(matrix, widget)) {
      // Determine reserved rows for this widget (cover-page itself is always at row 0)
      const widgetReserved = widget.type === "cover-page" ? 0 : reservedRows;
      // Find new position preserving colSpan and rowSpan
      const newPos = getNextAvailablePosition(
        matrix,
        widget.position.colSpan,
        widget.position.rowSpan ?? 1,
        widgetReserved
      );
      const relocated: WidgetConfig = {
        ...widget,
        position: {
          ...widget.position,
          row: newPos.row,
          col: newPos.col,
        },
      };
      result.push(relocated);
      // Update matrix with relocated widget
      matrix = calculateGridPlacement(result);
    } else {
      result.push(widget);
      matrix = calculateGridPlacement(result);
    }
  }

  return result;
}

// ── Gap Compaction (Story 5.6, Task 5.2) ─────────────────────────────

/**
 * Compacts widgets upward to fill vertical gaps after widget deletion.
 * Preserves horizontal position (col/colSpan) — only shifts rows upward.
 *
 * Strategy:
 * 1. Sort widgets by row (ascending), then col (ascending)
 * 2. For each widget, try to move it up row-by-row until collision
 * 3. Return compacted array
 *
 * @param reservedRows - Number of rows reserved at the top (e.g. 3 for cover page).
 *   Non-cover-page widgets will not be compacted into rows 0..(reservedRows-1).
 *   Cover-page widgets (type === "cover-page") are always placed at row 0.
 */
export function compactWidgets(widgets: WidgetConfig[], reservedRows = 0): WidgetConfig[] {
  if (widgets.length === 0) return [];

  // Sort by row then col for top-to-bottom, left-to-right processing
  const sorted = [...widgets].sort((a, b) => {
    if (a.position.row !== b.position.row) return a.position.row - b.position.row;
    return a.position.col - b.position.col;
  });

  const compacted: WidgetConfig[] = [];

  for (const widget of sorted) {
    const matrix = calculateGridPlacement(compacted);

    // Cover-page is always pinned at row 0
    const minRow = widget.type === "cover-page" ? 0 : Math.max(0, reservedRows);

    // Try to move widget up row by row
    let bestRow = widget.position.row;
    for (let tryRow = minRow; tryRow <= widget.position.row; tryRow++) {
      const candidate: WidgetConfig = {
        ...widget,
        position: { ...widget.position, row: tryRow },
      };

      // Extend matrix for collision check
      const neededRows = tryRow + (widget.position.rowSpan ?? 1);
      while (matrix.length < neededRows) {
        matrix.push(Array.from({ length: GRID_COLUMNS }, () => null));
      }

      if (!hasCollision(matrix, candidate)) {
        bestRow = tryRow;
        break;
      }
    }

    compacted.push({
      ...widget,
      position: { ...widget.position, row: bestRow },
    });
  }

  return compacted;
}

// ── Grid Position from Point (Story 5.6, Task 3) ────────────────────

/**
 * Converts a pointer coordinate to a grid position.
 * Accounts for canvas padding, scroll offset, and gaps.
 */
export function getGridPositionFromPoint(
  canvasRect: DOMRect,
  scrollTop: number,
  point: { x: number; y: number },
  colSpan: number,
  rowSpan: number,
  padding = 16
): { row: number; col: number; colSpan: number; rowSpan: number } {
  const contentWidth = canvasRect.width - 2 * padding;
  const cellWidth = (contentWidth - (GRID_COLUMNS - 1) * GRID_GAP) / GRID_COLUMNS;

  const relativeX = point.x - canvasRect.left - padding;
  const relativeY = point.y - canvasRect.top + scrollTop - padding;

  const col = Math.floor(relativeX / (cellWidth + GRID_GAP));
  const row = Math.floor(relativeY / (ROW_HEIGHT_UNIT + GRID_GAP));

  const clampedCol = Math.max(0, Math.min(col, GRID_COLUMNS - colSpan));
  const clampedRow = Math.max(0, row);
  return { row: clampedRow, col: clampedCol, colSpan, rowSpan };
}

// ── Smart Defaults & Auto-Sizing (Story 5.7) ────────────────────────

/** Widget types as a union for type safety in getSmartDefaults */
export type SmartDefaultWidgetType = "kpi" | "line-chart" | "bar-chart" | "pie-chart" | "data-table" | "text-block" | "top-posts" | "cover-page" | "funnel" | "demographic" | "heading" | "paragraph" | "rich-text" | "spacer-divider" | "image" | "embed" | "custom-metric" | "benchmark" | "goal";

/**
 * Returns optimal default colSpan and rowSpan for a widget type.
 * Used when adding new widgets to the canvas to provide sensible initial sizes.
 */
export function getSmartDefaults(
  type: SmartDefaultWidgetType,
  config?: { textLength?: number; rowCount?: number; legendItems?: number }
): { colSpan: ValidColSpan; rowSpan: number } {
  switch (type) {
    case "kpi":
      return { colSpan: 3, rowSpan: 1 };
    case "line-chart":
    case "bar-chart":
      return { colSpan: 6, rowSpan: config?.legendItems && config.legendItems > 4 ? 3 : 2 };
    case "pie-chart":
      return { colSpan: 4, rowSpan: config?.legendItems && config.legendItems > 6 ? 3 : 2 };
    case "data-table": {
      const tableRows = config?.rowCount ?? 5;
      return { colSpan: 12, rowSpan: Math.min(6, Math.max(2, Math.ceil(tableRows / 3))) };
    }
    case "text-block": {
      const textLen = config?.textLength ?? 100;
      return { colSpan: 12, rowSpan: Math.min(6, Math.max(1, Math.ceil(textLen / 200))) };
    }
    case "top-posts":
      return { colSpan: 6, rowSpan: 4 };
    case "cover-page":
      return { colSpan: 12, rowSpan: 3 };
    case "funnel":
      return { colSpan: 6, rowSpan: 3 };
    case "demographic":
      return { colSpan: 6, rowSpan: 2 };
    case "heading":
      return { colSpan: 12, rowSpan: 1 };
    case "paragraph": {
      const paraLen = config?.textLength ?? 100;
      return { colSpan: 12, rowSpan: Math.min(4, Math.max(1, Math.ceil(paraLen / 200))) };
    }
    case "rich-text": {
      const rtLen = config?.textLength ?? 150;
      return { colSpan: 12, rowSpan: Math.min(6, Math.max(2, Math.ceil(rtLen / 150))) };
    }
    case "spacer-divider":
      return { colSpan: 12, rowSpan: 1 };
    case "image":
      return { colSpan: 6, rowSpan: 3 };
    case "embed":
      return { colSpan: 12, rowSpan: 3 };
    case "custom-metric":
      return { colSpan: 3, rowSpan: 1 };
    case "benchmark":
      return { colSpan: 6, rowSpan: 4 };
    case "goal":
      return { colSpan: 4, rowSpan: 2 };
    default:
      return { colSpan: 6, rowSpan: 2 };
  }
}

// ── Configured-widget add planner (Story 37.3) ───────────────────────

const AUTO_SIZE_WIDGET_TYPES: ReadonlySet<string> = new Set(["text-block", "data-table"]);

/**
 * Pure planner for "add a configured widget to a page" — the guard + placement
 * logic behind the report editor's configure-then-add category panels
 * (Images/Embeds, via the route's `handleAddWidget`). Extracted here so the
 * business rules are unit-testable in isolation rather than buried in the route
 * component (build-to-sell: testable logic, one definition; code-review R3).
 *
 * Returns the fully-placed new widget, or `null` when the add must be REJECTED:
 *  - the page is already at `MAX_WIDGETS`, or
 *  - `type` is `cover-page` and the page already has one (one-cover-page-max).
 *
 * Placement + cover-page reserved-rows mirror `CanvasEditor.handleWidgetAdd`'s
 * non-cover-page branch exactly (same `calculateGridPlacement` +
 * `getNextAvailablePosition`), so the panel add path and the canvas add path
 * can't drift. `id` is injected by the caller so this stays pure (no
 * `crypto.randomUUID()` side effect); `autoSize` is set for the same types the
 * canvas marks auto-sizing (`text-block`, `data-table`).
 *
 * NOTE: this does NOT replicate `CanvasEditor.handleWidgetAdd`'s cover-page
 * shift-down/pin-to-top special case — the configure-then-add panels never emit
 * a `cover-page` (images/embeds only); the cover-page REJECTION guard is purely
 * defensive for any future panel wired through the same path.
 */
export function planConfiguredWidgetAdd(params: {
  widgets: WidgetConfig[];
  type: string;
  id: string;
  title: string;
  colSpan: ValidColSpan;
  rowSpan: number;
  config: Record<string, unknown>;
}): WidgetConfig | null {
  const { widgets, type, id, title, colSpan, rowSpan, config } = params;
  if (widgets.length >= MAX_WIDGETS) return null;
  if (type === "cover-page" && widgets.some((w) => w.type === "cover-page")) return null;

  const hasCoverPage = widgets.some((w) => w.type === "cover-page");
  const reservedRows = hasCoverPage ? 3 : 0;
  const matrix = calculateGridPlacement(widgets);
  const pos = getNextAvailablePosition(matrix, colSpan, rowSpan, reservedRows);

  return {
    id,
    type,
    title,
    position: { row: pos.row, col: pos.col, colSpan, rowSpan },
    config,
    ...(AUTO_SIZE_WIDGET_TYPES.has(type) ? { autoSize: true } : {}),
  };
}

/**
 * Plan a BATCH of preset widgets in ONE pass (Story 18.4 AC #9 — the combined
 * budget/goal dashboard). The position-less `PresetWidget`s are positioned
 * incrementally against a WORKING copy of the snapshot (each placement updates
 * the matrix), exactly mirroring CanvasEditor.handlePresetAdd — so a multi-
 * widget add never plans against a stale snapshot the way N sequential
 * single-adds would (each route single-add reads `activePageWidgetsRef.current`,
 * which only refreshes on the next render).
 *
 * All-or-nothing (the preset contract): returns the positioned widgets when the
 * whole batch fits, or `null` when there aren't enough slots — never a partial
 * preset. The caller appends the result in a single functional state update.
 *
 * `PresetWidget` is `Omit<WidgetConfig, "position"> & { colSpan; rowSpan }`,
 * declared in lib/widgetPresets.ts (a leaf import would create no cycle), but
 * gridUtils stays preset-agnostic — it accepts the minimal structural shape.
 */
export function planPresetWidgetsAdd(params: {
  widgets: WidgetConfig[];
  presetWidgets: Array<{
    id: string;
    type: string;
    title: string;
    config: Record<string, unknown>;
    colSpan: number;
    rowSpan: number;
    autoSize?: boolean;
  }>;
}): WidgetConfig[] | null {
  const { widgets, presetWidgets } = params;
  if (presetWidgets.length === 0) return [];
  if (widgets.length + presetWidgets.length > MAX_WIDGETS) return null;

  const hasCoverPage = widgets.some((w) => w.type === "cover-page");
  const reservedRows = hasCoverPage ? 3 : 0;
  const working = [...widgets];
  const positioned: WidgetConfig[] = [];

  for (const pw of presetWidgets) {
    const colSpan = pw.colSpan as ValidColSpan;
    const matrix = calculateGridPlacement(working);
    const pos = getNextAvailablePosition(matrix, colSpan, pw.rowSpan, reservedRows);
    const widget: WidgetConfig = {
      id: pw.id,
      type: pw.type,
      title: pw.title,
      position: { row: pos.row, col: pos.col, colSpan, rowSpan: pw.rowSpan },
      config: pw.config,
      ...(pw.autoSize || AUTO_SIZE_WIDGET_TYPES.has(pw.type)
        ? { autoSize: true }
        : {}),
    };
    positioned.push(widget);
    working.push(widget);
  }

  return positioned;
}

/** Font sizes by text-block variant (in px) */
const TEXT_VARIANT_FONT_SIZES: Record<string, number> = {
  heading: 20,
  body: 14,
  "ai-summary": 14,
};

/**
 * Estimates the rowSpan a text block needs based on its content.
 * Pure function with no DOM dependency — produces the same result every time.
 * This is the PRIMARY sizing source for PDF compatibility.
 *
 * @param contentWidthPx - Canvas content width in pixels (default: A4_CONTENT_WIDTH_PX for portrait).
 *   Pass SLIDE_CONTENT_WIDTH_PX (888) for slide-deck format.
 * @param rowHeightPx - Row height unit in pixels (default: ROW_HEIGHT_UNIT for portrait).
 *   Pass SLIDE_ROW_HEIGHT_UNIT_PX (54) for slide-deck format.
 */
export function estimateTextRowSpan(
  text: string,
  colSpan: ValidColSpan | number,
  variant: "heading" | "body" | "ai-summary" = "body",
  contentWidthPx: number = A4_CONTENT_WIDTH_PX,
  rowHeightPx: number = ROW_HEIGHT_UNIT
): number {
  if (!text) return 1;
  const AVG_CHAR_WIDTH = 0.55;
  const LINE_HEIGHT = 1.6;
  const fontSize = TEXT_VARIANT_FONT_SIZES[variant] ?? 14;
  // Pixel width from colSpan using content width, -32px padding
  const containerWidth = (colSpan / GRID_COLUMNS) * contentWidthPx - 32;
  const charsPerLine = Math.floor(containerWidth / (fontSize * AVG_CHAR_WIDTH));
  const lineCount = Math.ceil(text.length / Math.max(charsPerLine, 1));
  const contentHeight = lineCount * fontSize * LINE_HEIGHT + 32; // +32px for padding
  return Math.max(1, Math.min(Math.ceil(contentHeight / rowHeightPx), 8));
}

// ── Page Break Calculation (Task 5.3 → Story 5.13, Task 4.2) ─────────

import { calculatePageBreaks as sharedCalculatePageBreaks } from "@provenleap/types/report-rendering";

/**
 * Computes the effective height (in grid row units) for each grid row
 * based on the maximum rowSpan of any widget starting at that row.
 */
export function getRowHeights(
  widgets: WidgetConfig[]
): Map<number, number> {
  if (widgets.length === 0) return new Map();

  const heights = new Map<number, number>();
  for (const widget of widgets) {
    const { row, rowSpan = 1 } = widget.position;
    const current = heights.get(row) ?? 1;
    heights.set(row, Math.max(current, rowSpan));
  }

  return heights;
}

/**
 * Calculates page break positions based on cumulative row heights.
 * Delegates to the shared calculatePageBreaks() from @provenleap/types/report-rendering
 * with web px-based defaults. Same algorithm as PDF (pt-based), producing
 * identical break points when proportionally scaled.
 *
 * @param widgets - All widgets in the canvas
 * @param pageHeight - Usable A4 content height in pixels (default: 931px)
 * @param rowUnit - Height of 1 row unit in pixels (default: 80px)
 * @param gap - Gap between rows in pixels (default: 16px)
 */
export function calculatePageBreaks(
  widgets: WidgetConfig[],
  pageHeight: number = A4_CONTENT_HEIGHT_PX,
  rowUnit: number = ROW_HEIGHT_UNIT,
  gap: number = GRID_GAP
): number[] {
  return sharedCalculatePageBreaks(widgets, pageHeight, rowUnit, gap);
}

// ── Page Splitting for Preview (Story 5.10, Task 6) ──────────────

/**
 * Normalizes widget row positions within a page so the minimum row is 0.
 * Required for CSS Grid rendering — widgets must start at row 0 per page,
 * otherwise the grid creates empty rows above them.
 */
function normalizePageWidgetRows(pageWidgets: WidgetConfig[]): WidgetConfig[] {
  if (pageWidgets.length === 0) return pageWidgets;
  const minRow = Math.min(...pageWidgets.map((w) => w.position.row));
  if (minRow === 0) return pageWidgets;
  return pageWidgets.map((w) => ({
    ...w,
    position: { ...w.position, row: w.position.row - minRow },
  }));
}

/**
 * Splits widgets into pages based on page break boundaries.
 * Used by the full-screen preview modal for page-by-page navigation.
 *
 * Each "page" is a subset of widgets whose starting row falls within
 * the page boundary. Widgets are sorted by position (row, then col)
 * before splitting. Row positions are normalized per page so that
 * the first row starts at 0 (required for CSS Grid rendering).
 *
 * Edge case: A single widget taller than one page is NOT split —
 * it stays on its starting page and overflows.
 */
export function splitWidgetsIntoPages(
  widgets: WidgetConfig[],
  pageHeight: number = A4_CONTENT_HEIGHT_PX,
  rowUnit: number = ROW_HEIGHT_UNIT,
  gap: number = GRID_GAP
): WidgetConfig[][] {
  if (widgets.length === 0) return [];

  const breakRows = calculatePageBreaks(widgets, pageHeight, rowUnit, gap);

  // Sort widgets by row then col for consistent ordering
  const sorted = [...widgets].sort((a, b) => {
    if (a.position.row !== b.position.row) return a.position.row - b.position.row;
    return a.position.col - b.position.col;
  });

  // If no page breaks, everything is on one page
  if (breakRows.length === 0) {
    return [sorted];
  }

  const pages: WidgetConfig[][] = [];
  let currentPageWidgets: WidgetConfig[] = [];
  let breakIndex = 0;

  for (const widget of sorted) {
    // Check if this widget starts at or after the next page break
    while (
      breakIndex < breakRows.length &&
      widget.position.row >= breakRows[breakIndex]!
    ) {
      if (currentPageWidgets.length > 0) {
        pages.push(currentPageWidgets);
        currentPageWidgets = [];
      }
      breakIndex++;
    }
    currentPageWidgets.push(widget);
  }

  // Push last page
  if (currentPageWidgets.length > 0) {
    pages.push(currentPageWidgets);
  }

  // Normalize row positions per page so CSS Grid renders correctly
  return pages.map(normalizePageWidgetRows);
}

